import { gql, type QueryHookOptions, useQuery } from '@apollo/client';
import React, { useMemo, useState } from 'react';
import type { Asset, Query, QueryAssetsByNameWithFilterArgs } from '@/types/GraphqlSchema';
import { WEB_BASIC_SUBSCRIPTION } from '@/utils/constants';
import type { ValuationsFilters as ValuationsFiltersType } from '../../store/valuationsStore';
import { AutoCompleteSimpleComposable } from './AutoCompleteSimpleComposable';
import { FilterKey } from './filterConfig';

const ASSETS_BY_NAME_WITH_FILTER_QUERY = gql`
  query assetsByNameWithFilter(
    $search: String!
    $countries: [String!]
    $companies: [String!]
    $basins: [String!]
    $geologyEpochIds: [ID!]
    $nonOperator: Boolean
    $operator: Boolean
    $includeHistoricalParticipants: Boolean!
    $isMAndAOpportunity: Boolean
    $feature: String
  ) {
    assetsByNameWithFilter(
      search: $search
      countries: $countries
      companies: $companies
      basins: $basins
      nonOperator: $nonOperator
      operator: $operator
      geologyEpochIds: $geologyEpochIds
      includeHistoricalParticipants: $includeHistoricalParticipants
      isMAndAOpportunity: $isMAndAOpportunity
      feature: $feature
    ) {
      assets {
        id
        legacyId
        name
        displayName
        country {
          id
          name
          displayName
          isoCode
        }
      }
      totalCount
    }
  }
`;

type AssetsProps = {
  filters: ValuationsFiltersType;
  onChange: (assets: Asset[]) => void;
  context: string;
};

type AssetsQueryData = Pick<Query, 'assetsByNameWithFilter'>;

const name = 'Asset/Block';
const note =
  '*Only the top 100 assets will be shown, you can access more assets by typing in the name.';

const buildQueryVariables = (
  filters: ValuationsFiltersType,
  searchValue: string,
): QueryAssetsByNameWithFilterArgs => ({
  search: searchValue,
  countries: filters[FilterKey.Countries]?.map(country => country.isoCode) || [],
  companies: filters[FilterKey.Companies]?.map(company => company.name) || [],
  basins: filters[FilterKey.Basins]?.map(basin => basin.name) || [],
  geologyEpochIds: [],
  nonOperator: Boolean(filters[FilterKey.NonOperator]),
  operator: Boolean(filters[FilterKey.Operator]),
  includeHistoricalParticipants: Boolean(filters[FilterKey.IncludeHistoricalParticipants]),
  isMAndAOpportunity: filters[FilterKey.Opportunities] || false,
  feature: WEB_BASIC_SUBSCRIPTION,
});

export const AssetsFilter = ({ filters, onChange }: AssetsProps) => {
  const [searchValue, setSearchValue] = useState('');

  const queryVariables = buildQueryVariables(filters, searchValue);

  const queryOptions: QueryHookOptions<AssetsQueryData, QueryAssetsByNameWithFilterArgs> = {
    variables: queryVariables,
    notifyOnNetworkStatusChange: true,
  };

  const { data, loading } = useQuery<AssetsQueryData, QueryAssetsByNameWithFilterArgs>(
    ASSETS_BY_NAME_WITH_FILTER_QUERY,
    queryOptions,
  );

  const assets = Array.isArray(data?.assetsByNameWithFilter?.assets)
    ? data.assetsByNameWithFilter.assets
    : [];

  const assetsValue = useMemo(() => {
    const filterAssets = filters.assets;
    if (Array.isArray(filterAssets)) {
      return filterAssets;
    }
    if (filterAssets && typeof filterAssets === 'object') {
      return [filterAssets];
    }
    return [];
  }, [filters.assets]);

  return (
    <AutoCompleteSimpleComposable<Asset>
      options={assets}
      loading={loading}
      value={assetsValue}
      multiple={true}
      size='medium'
      getOptionLabel={option => (typeof option === 'string' ? option : option.name || '')}
      onChange={(_, newValue) => {
        onChange((newValue as Asset[]) || []);
      }}
      onInputChange={(_, newInputValue) => {
        setSearchValue(newInputValue);
      }}
      renderInput={params => (
        <AutoCompleteSimpleComposable.StyledInput
          params={params}
          label={name}
          data-id='assets-filter-input'
        />
      )}
      renderTags={(value, getTagProps) => {
        const numTags = value.length;
        return (
          <>
            {value.slice(0, 1).map((option, index) => (
              <AutoCompleteSimpleComposable.StyledTag
                key={option.id || index}
                option={option}
                index={index}
                getTagProps={getTagProps}
                getOptionLabel={opt => opt.name || ''}
              />
            ))}
            {numTags > 1 && ` +${numTags - 1}`}
          </>
        );
      }}
      renderOption={(props, option, state) => {
        const isLastOption = assets.indexOf(option) === assets.length - 1;

        return (
          <React.Fragment key={option.id}>
            <AutoCompleteSimpleComposable.Option
              {...props}
              option={option}
              state={state}
              getOptionLabel={opt => opt.name || ''}
            />
            {isLastOption && (
              <AutoCompleteSimpleComposable.Note key='note' note={note} isHidden={false} />
            )}
          </React.Fragment>
        );
      }}
      data-id='assets-filter'
    />
  );
};
